import {
  parseAdminVideoDetail,
  parseAdminVideosResponse,
  parseCompleteVideoUploadRequest,
  parseCompleteVideoUploadResponse,
  parseCreateAdminVideoRequest,
  parsePlaybackUrlResponse,
  parseUpdateAdminVideoRequest,
  parseVideoUploadSessionResponse,
  parseViewerVideoDetail,
  parseViewerVideosResponse
} from "@madamuh/domain";
import type {
  AdminVideoDetailDto,
  AdminVideosResponseDto,
  CompleteVideoUploadRequestDto,
  CompleteVideoUploadResponseDto,
  CreateAdminVideoRequestDto,
  PlaybackUrlResponseDto,
  UpdateAdminVideoRequestDto,
  VideoUploadSessionResponseDto,
  ViewerVideoDetailDto,
  ViewerVideosResponseDto
} from "@madamuh/domain";
import { apiGet, apiPatch, apiPost } from "./client";

export async function fetchAdminVideos(tenantSlug: string): Promise<AdminVideosResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/admin/tenants/${tenantSlug}/videos`);
  return parseAdminVideosResponse(response);
}

export async function createAdminVideo(
  tenantSlug: string,
  request: CreateAdminVideoRequestDto
): Promise<AdminVideoDetailDto> {
  const parsedRequest = parseCreateAdminVideoRequest(request);
  const response = await apiPost<CreateAdminVideoRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos`,
    parsedRequest
  );

  return parseAdminVideoDetail(response);
}

export async function createAdminVideoUploadSession(
  tenantSlug: string,
  videoId: string
): Promise<VideoUploadSessionResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos/${videoId}/upload-session`,
    {}
  );

  return parseVideoUploadSessionResponse(response);
}

export async function completeAdminVideoUpload(
  tenantSlug: string,
  videoId: string,
  request: CompleteVideoUploadRequestDto
): Promise<CompleteVideoUploadResponseDto> {
  const parsedRequest = parseCompleteVideoUploadRequest(request);
  const response = await apiPost<CompleteVideoUploadRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos/${videoId}/complete`,
    parsedRequest
  );

  return parseCompleteVideoUploadResponse(response);
}

export async function patchAdminVideo(
  tenantSlug: string,
  videoId: string,
  request: UpdateAdminVideoRequestDto
): Promise<AdminVideoDetailDto> {
  const parsedRequest = parseUpdateAdminVideoRequest(request);
  const response = await apiPatch<UpdateAdminVideoRequestDto, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos/${videoId}`,
    parsedRequest
  );

  return parseAdminVideoDetail(response);
}

export async function hideAdminVideo(tenantSlug: string, videoId: string): Promise<AdminVideoDetailDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos/${videoId}/hide`,
    {}
  );

  return parseAdminVideoDetail(response);
}

export async function republishAdminVideo(tenantSlug: string, videoId: string): Promise<AdminVideoDetailDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/admin/tenants/${tenantSlug}/videos/${videoId}/republish`,
    {}
  );

  return parseAdminVideoDetail(response);
}

export async function fetchFamilyVideos(tenantSlug: string): Promise<ViewerVideosResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/app/tenants/${tenantSlug}/videos`);
  return parseViewerVideosResponse(response);
}

export async function fetchFamilyVideoDetail(
  tenantSlug: string,
  videoId: string
): Promise<ViewerVideoDetailDto> {
  const response = await apiGet<unknown>(`/api/v1/app/tenants/${tenantSlug}/videos/${videoId}`);
  return parseViewerVideoDetail(response);
}

export async function createFamilyPlaybackUrl(
  tenantSlug: string,
  videoId: string
): Promise<PlaybackUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/app/tenants/${tenantSlug}/videos/${videoId}/playback-url`,
    {}
  );

  return parsePlaybackUrlResponse(response);
}

export async function fetchTeacherVideos(tenantSlug: string): Promise<ViewerVideosResponseDto> {
  const response = await apiGet<unknown>(`/api/v1/teacher/tenants/${tenantSlug}/videos`);
  return parseViewerVideosResponse(response);
}

export async function createTeacherPlaybackUrl(
  tenantSlug: string,
  videoId: string
): Promise<PlaybackUrlResponseDto> {
  const response = await apiPost<Record<string, never>, unknown>(
    `/api/v1/teacher/tenants/${tenantSlug}/videos/${videoId}/playback-url`,
    {}
  );

  return parsePlaybackUrlResponse(response);
}
